<template>
  <div class="carrousel-alarm-wrapper" style="width: 350px">
    <div v-if="!dataEvent.length">{{ $t("alarm.noImagesInCarrousel") }}</div>
    <div v-else ref="swiperEl" class="swiper carrousel-alarm-table">
      <div class="swiper-wrapper">
        <div
          v-for="(dataEventIndividual, key) in dataEvent"
          :key="`slide-${key}`"
          class="swiper-slide"
          style="min-height: 150px; position: relative"
          @click="slideClick(dataEventIndividual.urlImg)"
          v-b-modal.image_modal
        >
          <div style="position: absolute; width: 100%; height: 100%; z-index: 3">
            <div
              style="
                color: white;
                width: 100%;
                background-color: rgba(0, 0, 0, 0) !important;
                text-shadow: 1px 2px #000;
                padding: 0.5rem;
                top: 0;
                background: linear-gradient(0deg, transparent 0%, rgba(0, 0, 0, 0.5) 95%);
              "
            >
              <span>{{ dataEventIndividual.cameraName }}</span>
              <span style="float: right">{{
                parseDate(dataEventIndividual.dateEvent)
              }}</span>
            </div>

            <div
              style="
                position: absolute;
                color: white;
                width: 100%;
                background-color: rgba(0, 0, 0, 0) !important;
                text-shadow: 1px 2px #000;
                padding: 0.5rem;
                bottom: 0;
                background: linear-gradient(transparent, rgba(0, 0, 0, 0.5));
              "
            >
              <span style="bottom: 0">
                {{ dataEventIndividual.inferiorData }}
              </span>
              <span v-if="dataEventIndividual.secondInferiorData" style="float: right">
                {{ dataEventIndividual.secondInferiorData }}
              </span>
            </div>
          </div>
          <img
            v-if="dataEventIndividual.urlImg && !isSlideEmpty(key)"
            :src="dataEventIndividual.urlImg"
            style="width: 350px; display: block"
            @load="onSlideImgLoad(key)"
            @error="onSlideImgError(key)"
          />
          <div v-if="isSlideEmpty(key)" class="carrousel-alarm-state">
            <feather-icon icon="ImageIcon" size="28" />
            <span>{{ $t("alarm.imageNotAvailable") }}</span>
          </div>
          <div v-else-if="isSlideLoading(key)" class="carrousel-alarm-state">
            <b-spinner label="Spinning" variant="primary"></b-spinner>
            <span>{{ $t("alarm.loadingImage") }}</span>
          </div>
        </div>
      </div>
      <div class="swiper-button-prev"></div>
      <div class="swiper-button-next"></div>
    </div>
  </div>
</template>

<script>
import Swiper from "swiper/swiper-bundle.mjs";
import "swiper/swiper-bundle.css";
import { ref, onMounted, onBeforeUnmount, nextTick, set } from "@vue/composition-api";
import store from "@/store";
import dateParser from "@/libs/date-parser";
import axios from "@axios";

export default {
  name: "Carrousel",
  props: {
    idAlarm: Number,
    detEvent: Object,
    allAlarmData: Object,
    alarmType: String,
  },
  setup(props) {
    const dataEvent = ref([]);
    const swiperEl = ref(null);
    let swiper = null;
    let visibilityObserver = null;
    let initAttempts = 0;
    const retriedSlides = new Set();
    // Picture state per slide: falsy = still loading, "ready" | "empty"
    const slideState = ref({});

    const setSlideState = (index, state) => set(slideState.value, index, state);
    const isSlideEmpty = (index) => slideState.value[index] === "empty";
    const isSlideLoading = (index) => !slideState.value[index];

    const initSwiper = () => {
      if (swiper) {
        refreshSwiper();
        return;
      }
      if (!dataEvent.value.length || !swiperEl.value) return;
      if (!swiperEl.value.offsetWidth && initAttempts < 10) {
        initAttempts++;
        requestAnimationFrame(initSwiper);
        return;
      }
      swiper = new Swiper(swiperEl.value, {
        slidesPerView: 1,
        autoHeight: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        on: {
          slideChange: (sw) => pageChange(sw.activeIndex),
        },
      });
      nextTick(() => refreshSwiper());
    };

    const refreshSwiper = () => {
      if (!swiper) return;
      swiper.update();
      swiper.updateAutoHeight(0);
      swiper.slideTo(swiper.activeIndex, 0);
    };

    const onSlideImgLoad = (index) => {
      setSlideState(index, "ready");
      refreshSwiper();
    };

    // A stale or unreachable url leaves a broken image; ask the events endpoint
    // once for that slide, then give up and show the empty state. Retrying a
    // single time keeps a failing url from looping, since an unchanged src does
    // not trigger @error again.
    const onSlideImgError = (index) => {
      if (retriedSlides.has(index)) {
        setSlideState(index, "empty");
        return;
      }
      retriedSlides.add(index);
      pageChange(index);
    };

    const observeVisibility = () => {
      if (!swiperEl.value) return;
      visibilityObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          initSwiper();
        }
      });
      visibilityObserver.observe(swiperEl.value);
    };

    const pageChange = (pageNumber) => {
      if (props.alarmType != "ar") {
        axios
          .post(
            `${window.__env.VUE_APP_BASE_URL}/v1/services/detection-events/get-events-by-alarm-id`,
            {
              alarmId: props.idAlarm,
              index: pageNumber,
            }
          )
          .then((res) => {
            const response = res.data;
            let eventData;

            if (response.data.event_type === "lpr") {
              eventData = {
                urlImg: response.data.general_image_url || response.data.image_url,
                cameraName: response.data.camera_data.camera.name,
                dateEvent: response.data.event_created_at,
                inferiorData: dataEvent.value[0]?.inferiorData,
              };
            } else if (response.data.event_type === "fr") {
              eventData = {
                urlImg: response.data.data.images[0],
                cameraName: response.data.data.camera_name,
                dateEvent: response.data.data.event_created_at,
                inferiorData: response.data.data.match_person_name,
                secondInferiorData: response.data.watch_list || "",
              };
            } else {
              eventData = {
                urlImg: response.data.data.images[0],
                cameraName: response.data.data.camera_name,
                dateEvent: response.data.event_created_at,
                inferiorData: dataEvent.value[0]?.inferiorData,
              };
            }

            set(dataEvent.value, pageNumber, eventData);
            setSlideState(pageNumber, eventData.urlImg ? null : "empty");
            nextTick(() => swiper && swiper.update());
          })
          // Nothing else to wait for: let the slide show the empty state
          .catch(() => setSlideState(pageNumber, "empty"));
      } else {
        axios
          .post(`${window.__env.VUE_APP_BASE_URL}/v1/analytic/analyticRecordByIndex`, {
            alarmId: props.idAlarm,
            index: pageNumber,
          })
          .then((res) => {
            const response = res.data;
            const eventData = {
              urlImg: response.data?.media[0]?.url,
              cameraName: response.data.camera.name,
              dateEvent: response.data.created_at,
              inferiorData: dataEvent.value[0]?.inferiorData,
            };
            set(dataEvent.value, pageNumber, eventData);
            setSlideState(pageNumber, eventData.urlImg ? null : "empty");
            nextTick(() => swiper && swiper.update());
          })
          // Nothing else to wait for: let the slide show the empty state
          .catch(() => setSlideState(pageNumber, "empty"));
      }
    };

    const getAiMessage = (data) => {
      const aiImageContext = data?.find(
        (aiElement) => aiElement.type === "ai_image_context"
      );
      return aiImageContext?.data.context;
    };

    // dateParser es una clase con métodos estáticos; la envolvemos en una función
    // para exponerla a la plantilla sin que setup la convierta en binding reactivo.
    const parseDate = (value) => dateParser.parseDate(value);

    const slideClick = async (imageUrl) => {
      const aiData = props.allAlarmData?.data;
      await store.dispatch("image_modal/imageUrl", imageUrl);
      if (!aiData) return;
      store.dispatch("image_modal/aiComment", getAiMessage(aiData));
    };

    onMounted(() => {
      let urlImage = "";
      let totalMatches = 1;
      let inferiorData = null;
      let secondInferiorData = null;

      if (!props.detEvent) {
        return;
      }

      if (props.alarmType !== "lpr" && props.alarmType !== "ar") {
        totalMatches = props.detEvent?.total_events || 0;
        //get url images from the other alarms except types (fr and lpr)
        urlImage =
          props.detEvent?.data?.media?.[0]?.url ||
          props.detEvent?.data?.data?.images?.[0] ||
          props.detEvent?.media?.[0]?.url ||
          props.detEvent?.detection_event_data?.media[0]?.url ||
          props.detEvent?.data?.images?.[0] || //brako
          "";
      }

      if (props.alarmType === "fr") {
        if (props.detEvent?.provider === "ntechlab") {
          inferiorData = props.detEvent.data?.match_person_name;
          secondInferiorData = props.detEvent.watch_list || "";
        }
        if (props.detEvent?.event_type === "lpr") {
          // Non-LPR alarms (e.g. speeding) can carry an lpr-shaped detection
          // event; those expose general_image_url/image_url instead of data.images
          urlImage =
            props.detEvent?.general_image_url || props.detEvent?.image_url || "";
          inferiorData = props.detEvent?.data?.plate_number || null;
          if (props.detEvent?.data?.speed) {
            secondInferiorData = `${props.detEvent.data.speed} km/h`;
          }
        } else {
          urlImage = props.detEvent?.data?.images?.[0] || "";
        }
        totalMatches = props.detEvent?.total_events || 0;
      }

      if (props.alarmType === "lpr") {
        totalMatches = props.detEvent?.total_events || 0;
        // Depending on the provider, the alarm list payload may only carry the
        // plate crop (image_url) instead of the overall picture
        urlImage =
          props.detEvent?.general_image_url ||
          props.detEvent?.image_url ||
          props.detEvent?.data?.images?.[0] ||
          "";
        inferiorData = props.detEvent?.data?.plate_number;
      }

      if (props.alarmType === "detectionEvent") {
        inferiorData = props.detEvent?.event_type;
      }

      if (props.alarmType === "ar") {
        inferiorData = props.detEvent?.analytic_record?.priority;
        urlImage = props.detEvent?.detection_event_data?.media[0]?.url || "";
        totalMatches = props.detEvent?.analytic_record_count || 0;
      }

      //define this structure for all alarms
      dataEvent.value.push({
        urlImg: urlImage,
        cameraName:
          props.detEvent?.data?.camera_name ||
          props.detEvent?.detection_event_data?.camera_data?.camera?.name,
        dateEvent:
          props.detEvent?.event_created_at ||
          props.detEvent?.detection_event_data?.created_at,
        inferiorData,
        secondInferiorData,
      });
      //this at the end of mounted need set totalEvents
      for (let index = 1; index <= totalMatches - 1; index++) {
        dataEvent.value[index] = {};
      }

      // When the list payload has no usable picture, fetch the first event the
      // same way a slide change does, instead of leaving a permanent spinner
      if (!urlImage) {
        if (totalMatches > 0) {
          pageChange(0);
        } else {
          setSlideState(0, "empty");
        }
      }

      nextTick(() => observeVisibility());
    });

    onBeforeUnmount(() => {
      if (visibilityObserver) {
        visibilityObserver.disconnect();
        visibilityObserver = null;
      }
      if (swiper) {
        swiper.destroy(true, true);
        swiper = null;
      }
    });

    return {
      dataEvent,
      swiperEl,
      parseDate,
      slideClick,
      onSlideImgLoad,
      onSlideImgError,
      isSlideEmpty,
      isSlideLoading,
    };
  },
};
</script>

<style>
.carrousel-alarm-table .swiper-button-prev,
.carrousel-alarm-table .swiper-button-next {
  color: #1c3d5e;
}

.carrousel-alarm-table .swiper-button-prev::after,
.carrousel-alarm-table .swiper-button-next::after {
  font-size: 24px;
  font-weight: bold;
}

.carrousel-alarm-table .swiper-button-prev {
  left: 40px;
}

.carrousel-alarm-table .swiper-button-next {
  right: 40px;
}

.carrousel-alarm-state {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 150px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.75;
}

.carrousel-alarm-state span {
  margin-top: 0.5rem;
}
</style>
